import { buildDeletedLotResolver, loadDeletedLots } from './deletedLots'

// Findings #206: Reportes leaves out the records of deleted lots, as Biomasa,
// Panel, Base de datos, Agua and Alimentación do, so report totals match those
// screens. The records are kept (a later report will list them on purpose,
// finding #205); Reportes says of how many deleted lots records were left out.
//
// `prepareReportLotFilter` loads the deleted lots before a run; until then (or
// offline before the first load) nothing is left out, as before.

type RecordLotFields = { id?: unknown; lotId?: unknown; lot?: unknown; unmappedLot?: unknown; productionUnitId?: unknown; recordedAt?: unknown; date?: unknown }

let resolver: ((row: RecordLotFields, fallbackUnitId?: string) => string | null) | null = null
const leftOut = new Set<string>()

export const prepareReportLotFilter = async (args: { farmIds: string[]; lots: ReadonlyArray<{ id: unknown; lotKey?: unknown; productionUnitId?: unknown; createdAt?: unknown }> }): Promise<void> => {
  leftOut.clear()
  const deleted = (await Promise.all(args.farmIds.map((farmId) => loadDeletedLots(farmId).catch(() => null))))
    .flatMap((list) => list ?? [])
  resolver = deleted.length > 0
    ? buildDeletedLotResolver(deleted, args.lots.map((lot) => ({
      id: String(lot.id),
      lotKey: String(lot.lotKey ?? ''),
      ...(lot.productionUnitId ? { productionUnitId: String(lot.productionUnitId) } : {}),
      ...(lot.createdAt ? { createdAt: String(lot.createdAt) } : {})
    })))
    : null
}

/**
 * The records that do not belong to a deleted lot; the deleted lots they
 * belong to are counted as left out (findings #208: by the deleted lot, so the
 * arrival record of "Sembrar en varias unidades" or a name-only record is not
 * an extra lot).
 */
export const withoutDeletedLotRecords = <T extends object>(records: T[], fallbackUnitId?: string): T[] => {
  const resolveDeleted = resolver
  if (!resolveDeleted || !Array.isArray(records)) return records
  return records.filter((record) => {
    const deletedLotId = resolveDeleted(record as RecordLotFields, fallbackUnitId)
    if (deletedLotId === null) return true
    leftOut.add(deletedLotId)
    return false
  })
}

/** `withoutDeletedLotRecords` over a repository read (a promise or a plain list). */
export const currentRecords = <T extends object>(records: Promise<T[]> | T[]): Promise<T[]> => (
  Promise.resolve(records).then((list) => withoutDeletedLotRecords(list))
)

/** Of how many deleted lots the last run left records out. */
export const deletedLotRecordsLeftOut = (): number => leftOut.size

/** Test hook: forget the loaded deleted lots. */
export const resetReportLotFilter = (): void => {
  resolver = null
  leftOut.clear()
}
